import { useQuery, useTRPC } from "@luckymenu/api-client/react";
import type { KotStatus } from "@luckymenu/core";
import { useCallback, useMemo } from "react";

import { kitchenView, type PendingKots, withKotChanges } from "../ordering/kots";
import { type KotStatusEntry, kotChangeId } from "../ordering/outbox";
import type { OrderingProblem } from "../ordering/problems";
import { useStaffCore } from "../provider";
import { useCan } from "./use-navigation";
import { useOutbox, useRestaurantId } from "./use-order";

function kotEntries(entries: ReturnType<typeof useOutbox>["entries"]): KotStatusEntry[] {
	return entries.filter((e): e is KotStatusEntry => e.kind === "setKotStatus");
}

/**
 * KOTs being marked done or open again from this device and not confirmed
 * yet (the outbox), so every screen shows the change at once, also offline.
 * A refused change drops out and the server's state shows again.
 */
export function useKotChanges(): PendingKots {
	const { entries } = useOutbox();
	return useMemo(() => {
		const map = new Map<string, KotStatus>();
		for (const e of kotEntries(entries))
			if (e.state !== "failed") map.set(e.input.kotId, e.input.status);
		return map;
	}, [entries]);
}

/**
 * The kitchen's list (P3-T28): open KOTs oldest first, and the last 20 done.
 * Every ordering event refetches it (a batch accepted adds tickets, a
 * cancelled item adds a cancel ticket, `kot.updated` moves one), so every
 * kitchen screen agrees. Needs `order.view`.
 */
export function useKitchenKots() {
	const trpc = useTRPC();
	const canView = useCan("order.view");
	const open = useQuery({
		...trpc.staff.ordering.listKots.queryOptions({ status: "open", limit: 100 }),
		enabled: canView,
	});
	const done = useQuery({
		...trpc.staff.ordering.listKots.queryOptions({ status: "done", limit: 20 }),
		enabled: canView,
	});
	const pending = useKotChanges();
	const view = useMemo(
		() =>
			kitchenView(
				withKotChanges(
					[...(open.data ?? []), ...(done.data ?? [])],
					pending,
					new Date().toISOString(),
				),
			),
		[open.data, done.data, pending],
	);
	return {
		status:
			open.data && done.data
				? ("ready" as const)
				: open.isError || done.isError
					? ("error" as const)
					: ("loading" as const),
		retry: () => {
			void open.refetch();
			void done.refetch();
		},
		view,
	};
}

export type KotProblem = { kotId: string; problem: OrderingProblem };

/**
 * "Done" on a KOT, and "Open again" (every role: `kot.update`). Goes
 * through the outbox (P3-T29): the ticket moves at once on this device's
 * screens, also without a connection, and reaches the server when it can.
 * A refusal brings the ticket back with `problem`; `clearProblem` forgets it.
 */
export function useKotStatus() {
	const core = useStaffCore();
	const allowed = useCan("kot.update");
	const outbox = useOutbox();
	const restaurantId = useRestaurantId();
	const failed = kotEntries(outbox.entries).find((e) => e.state === "failed") ?? null;
	const set = useCallback(
		(kotId: string, status: KotStatus, orderId: string) => {
			if (!allowed || !restaurantId) return false;
			// A refused change of this ticket is replaced by the new one.
			for (const e of kotEntries(core.outbox.getEntries()))
				if (e.input.kotId === kotId && e.state === "failed") core.outbox.dismiss(e.id);
			core.outbox.add({
				kind: "setKotStatus",
				id: kotChangeId(kotId, status),
				restaurantId,
				orderId,
				input: { kotId, status },
			});
			return true;
		},
		[allowed, restaurantId, core],
	);
	return {
		allowed,
		done: (kot: { id: string; orderId: string }) => set(kot.id, "done", kot.orderId),
		reopen: (kot: { id: string; orderId: string }) => set(kot.id, "open", kot.orderId),
		problem: failed
			? ({ kotId: failed.input.kotId, problem: failed.problem ?? "failed" } as KotProblem)
			: null,
		clearProblem: useCallback(() => {
			for (const e of kotEntries(core.outbox.getEntries()))
				if (e.state === "failed") core.outbox.dismiss(e.id);
		}, [core]),
	};
}
